<script setup>
import HospitalList from "@/components/hpo/dashboard/hospital/HospitalList";
import {useQuery} from "@vue/apollo-composable";
import {useLastResult} from "@/graphql/composable";
import {HpoHospitalList} from "@/graphql/queries/hpo/hpoHospitalList.graphql"
import {ContactPointPatientListQuery} from "@/graphql/queries/hpo/patients.graphql"
import {HpoBodyStatus, HpoRole} from "../choices";
import {computed, onBeforeUnmount, onMounted, provide, ref, useTemplateRef, watch} from "vue";
import TransportReplyModal from "./patientListItems/TransportReplyModal.vue";
import {mdiChevronDoubleDown, mdiDownload} from "@mdi/js";
import {useSearch} from "@/search";
import CountsBlock from "@/components/hpo/dashboard/CountsBlock";
import DashboardModalsContainer from "@/components/hpo/dashboard/DashboardModalsContainer.vue";
import QrScanButton from "@/components/hpo/QrScanButton.vue";
import dayjs from "dayjs";
import PatientListTab from "./patientListTabs/PatientListTab.vue";
import {useDisplay} from "vuetify/framework";
import ContactPointPatientList from "./patientListItems/ContactPointPatientList.vue";


const {smAndUp} = useDisplay()

const props = defineProps({
  hpo: {
    type: Object,
    required: true
  },
  member: {
    type: Object,
    required: true
  },
  patientCounts: {
    type: Object,
    required: true
  },
  patientCountsLoading: {
    type: Boolean,
    required: true
  },
  hpoPassword: {
    type: String,
    required: true
  },
  countries: {
    type: Array,
    required: true
  },
  mobileView: {
    type: Boolean,
    required: true,
  },
  chipProperties: {
    type: Object,
    required: true
  },
  personsTrackerTimestamp: {
    type: Object,
    required: true
  },
  hospitalsTrackerTimestamp: {
    type: Object,
    required: false
  }
})

const now = ref(new dayjs())
const modals = useTemplateRef('modals');
const editedTransportData = ref(null)
const transportRequestReplyData = ref(null)

// Persons Query
const personsQueryVariables = computed(() => ({
  updateTimestamp: props.personsTrackerTimestamp,
  hpoId: props.hpo.id,
  hospitalIds: props.member.hospitals
}));
const {
  result: patientListResult,
  loading: patientListLoading
} = useQuery(ContactPointPatientListQuery, personsQueryVariables)
const patientList = useLastResult(patientListResult, [], data => data.cpPatientList)
const alertList = useLastResult(patientListResult, [], data => data.alertList.alerts)

const {query, searchResultsOrAllItems: filteredPatientList} = useSearch(
  patientList, ['firstName', 'lastName', 'birthYear', 'birthNumber', 'note'], ['tic', 'temporaryIdentifier']
);

// Hospitals Query
const hospitalsQueryVariables = computed(() => ({
  updateTimestamp: props.hospitalsTrackerTimestamp,
  hpoId: props.hpo.id
}));
const {
  result: hospitalsResult,
  loading: hospitalListLoading
} = useQuery(HpoHospitalList, hospitalsQueryVariables)
const hospitalList = useLastResult(hospitalsResult, [], data => data.hpoHospitalList)

function showActionsModal(patient) {
  modals.value.showActionsModal(patient);
}

const anyLoading = computed(() => {
  return hospitalListLoading.value || patientListLoading.value || props.patientCountsLoading
})

const patientsNotInHospital = computed(() => {
  const list = patientList.value.filter((patient) => patient.status !== HpoBodyStatus.IN_HOSPITAL)
  return list.sort((a, b) => a.transportStatus - b.transportStatus)
})

const patientsInHospital = computed(() => {
  return patientList.value.filter((patient) => patient.status === HpoBodyStatus.IN_HOSPITAL)
})

const patientsWithAlarm = computed(() => {
  return patientList.value.filter((patient) => alertList.value.includes(parseInt(patient.id)))
})

const patientsWithIncompleteHospitalIdentification = computed(() => {
  return patientList.value.filter((patient) => incompleteHospitalIdentification(patient))
})

function incompleteHospitalIdentification(patient) {
  return !((
    patient.firstName &&
    patient.lastName &&
    patient.birthNumber &&
    patient.insurance
  ))
}

const lastClickedTab = ref('notInHospital')
const activeTab = ref(lastClickedTab.value)
const tab = computed({
  get() {
    return activeTab.value
  },
  set(newValue) {
    lastClickedTab.value = newValue
    activeTab.value = newValue
  }
})

watch(query, async (newValue, previousValue) => {
  if (newValue) {
    activeTab.value = 'search'
  } else {
    activeTab.value = lastClickedTab.value
  }
});

const tabs = computed(() => {
  return [
    {
      label: pgettext('label', 'going to hospital'),
      value: 'notInHospital',
      badgeContent: patientsNotInHospital.value.length,
    },
    {
      label: pgettext('hpo_body_status', 'in hospital'),
      value: HpoBodyStatus.IN_HOSPITAL,
      badgeContent: patientsInHospital.value.length,
    },
    {
      label: pgettext('label', 'incomplete identification'),
      value: 'incomplete',
      badgeContent: patientsWithIncompleteHospitalIdentification.value.length,
    },
    {
      label: pgettext('label', 'all patients'),
      value: 'all',
      badgeContent: patientList.value.length,
    },
    {
      label: pgettext('noun', 'search'),
      value: "search",
      badgeContent: filteredPatientList.value.length,
    }]
})

const dropdownActiveTab = computed(() => {
  if (tab.value === 'alarm') {
    return {
      label: pgettext('label', 'Patients lists'),
      badgeContent: null
    }
  }
  return tabs.value.find((item) => item.value === tab.value)
})


function handleOpenReplyModal(reply, patient) {
  transportRequestReplyData.value = {
    reply: reply,
    patient: patient
  }
}

provide('handleOpenReplyModal', handleOpenReplyModal)

function doAlert(patient) {
  return false
}

let updateNowInterval = null

function updateNow() {
  updateNowInterval = setInterval(() => {
    now.value = new dayjs()
  }, 5000)
}


onMounted(() => {
  updateNow()
})

onBeforeUnmount(() => {
  if (updateNowInterval) {
    clearInterval(updateNowInterval)
  }
})


</script>

<template>
  <v-container fluid class="pa-0">
    <v-row dense>
      <v-col class="d-flex ga-3 flex-wrap">
        <CountsBlock :hpoId="hpo.id"
                     :title="pgettext('label', 'Estimated on scene')"
                     :member="member"
                     :loading="patientCountsLoading"
                     :patientCounts="patientCounts.onScene"
                     :legend="pgettext('label', 'Estimated number of persons on scene')"/>

        <HospitalList v-if="[HpoRole.ZOS, HpoRole.CONTACT_POINT].includes(member.role)"
                      :hpoId="hpo.id"
                      :hospitalList="hospitalList"
                      :member="member"/>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="auto" class="d-flex align-center">
        <QrScanButton @scan="query = $event.tic"/>
      </v-col>
      <v-col>
        <!-- SEARCH BAR -->
        <div class="d-flex align-center ga-4 my-3">
          <v-text-field clearable
                        variant="outlined"
                        :hide-spin-buttons="true"
                        :loading="patientListLoading"
                        :label="pgettext('title', 'Patient search')"
                        :placeholder="pgettext('label', 'Put in TIC Card number / name / birth year / temporary identifier / birth number / note')"
                        hide-details="auto"
                        v-model="query"/>
          <v-btn :href="reverseUrl('web:hpo:download_patients', hpo.id)" :prependIcon="mdiDownload">
            {{ pgettext('action', 'Export patients') }}
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-card>
          <div class="d-flex">
            <PatientListTab label="ALARM" value="alarm"
                            variant="outlined"
                            :badgeContent="patientsWithAlarm.length"
                            :doAlert="patientsWithAlarm.length > 0"
                            :class="{'v-tab--selected': tab==='alarm'}"
                            @click="tab='alarm'"/>

            <v-tabs v-model="tab" center-active show-arrows grow>
              <template v-for="item in tabs">
                <PatientListTab v-show="smAndUp"
                                :label="item.label"
                                :value="item.value"
                                :badgeContent="item.badgeContent"/>
              </template>
              <v-menu v-if="!smAndUp">
                <template v-slot:activator="{ props }">
                  <v-btn
                    class="align-self-center me-4"
                    height="100%"
                    rounded="0"
                    variant="outlined"
                    v-bind="props"
                  >
                    <template v-slot:append
                              v-if="dropdownActiveTab.badgeContent !== null">
                      <v-badge
                        color="info"
                        :content="dropdownActiveTab.badgeContent"
                        inline
                      ></v-badge>
                    </template>
                    {{ dropdownActiveTab.label }}

                    <template v-slot:prepend>
                      <v-icon :icon="mdiChevronDoubleDown" end></v-icon>
                    </template>
                  </v-btn>
                </template>

                <v-list class="bg-grey-lighten-3">
                  <v-list-item
                    v-for="item in tabs"
                    :key="item.value"
                    :title="item.label"
                    @click="tab = item.value">

                    <template v-slot:append>
                      <v-badge
                        color="info"
                        :content="item.badgeContent"
                        inline
                      ></v-badge>
                    </template>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-tabs>
          </div>
          <v-card-text>
            <v-tabs-window v-model="tab">
              <v-tabs-window-item value="alarm" :transition="false">
                <ContactPointPatientList :patientList="patientsWithAlarm"
                                         :hpo="hpo"
                                         :member="member"
                                         :anyLoading="anyLoading"
                                         :hospitalList="hospitalList"
                                         :alertList="alertList"
                                         :now="now"
                                         @displayActionsModal="showActionsModal"/>
              </v-tabs-window-item>
              <v-tabs-window-item value="notInHospital"
                                  :transition="false">
                <ContactPointPatientList :patientList="patientsNotInHospital"
                                         :hpo="hpo"
                                         :member="member"
                                         :anyLoading="anyLoading"
                                         :hospitalList="hospitalList"
                                         :alertList="alertList"
                                         :now="now"
                                         @displayActionsModal="showActionsModal"/>
              </v-tabs-window-item>
              <v-tabs-window-item :value="HpoBodyStatus.IN_HOSPITAL"
                                  :transition="false">
                <ContactPointPatientList :patientList="patientsInHospital"
                                         :hpo="hpo"
                                         :member="member"
                                         :anyLoading="anyLoading"
                                         :hospitalList="hospitalList"
                                         :alertList="alertList"
                                         :now="now"
                                         @displayActionsModal="showActionsModal"/>
              </v-tabs-window-item>

              <v-tabs-window-item value="incomplete"
                                  :transition="false">
                <ContactPointPatientList :patientList="patientsWithIncompleteHospitalIdentification"
                                         :hpo="hpo"
                                         :member="member"
                                         :anyLoading="anyLoading"
                                         :hospitalList="hospitalList"
                                         :alertList="alertList"
                                         :now="now"
                                         @displayActionsModal="showActionsModal"/>
              </v-tabs-window-item>

              <v-tabs-window-item value="search" :transition="false">
                <ContactPointPatientList :patientList="filteredPatientList"
                                         :hpo="hpo"
                                         :member="member"
                                         :anyLoading="anyLoading"
                                         :hospitalList="hospitalList"
                                         :alertList="alertList"
                                         :now="now"
                                         @displayActionsModal="showActionsModal"/>
              </v-tabs-window-item>

              <v-tabs-window-item value="all" :transition="false">
                <ContactPointPatientList :patientList="patientList"
                                         :hpo="hpo"
                                         :member="member"
                                         :anyLoading="anyLoading"
                                         :hospitalList="hospitalList"
                                         :alertList="alertList"
                                         :now="now"
                                         @displayActionsModal="showActionsModal"/>
              </v-tabs-window-item>

            </v-tabs-window>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>


  </v-container>

  <DashboardModalsContainer ref="modals"
                            :countries="countries"
                            :member="member"
                            :hpo="hpo"
                            :patientList="patientList"/>

  <TransportReplyModal
    v-if="transportRequestReplyData"
    :reply="transportRequestReplyData.reply"
    :patient="transportRequestReplyData.patient"
    @close="transportRequestReplyData = null"
  />
</template>


<style scoped>
.patients-table thead th {
  background-color: #E8EAF6 !important;
  font-weight: bold !important;
  padding: 8px 8px !important;
  text-align: center !important;
}

.patients-table thead th.text-left {
  text-align: left !important;
}

.patients-table td {
  padding: 0 8px !important;
  text-align: center;
}
</style>
